"use client";
import React, { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const leistungen = [
  {
    icon: "WrenchScrewdriverIcon",
    title: "PC-Zusammenbau",
    desc: "Vollständiger Zusammenbau deines Systems – von CPU-Montage über Kühler bis hin zu Grafikkarte und Speicher.",
    tag: "Kernleistung",
  },
  {
    icon: "ComputerDesktopIcon",
    title: "Windows & Treiber",
    desc: "Saubere Windows-Installation, alle aktuellen Treiber, Windows-Updates und Grundkonfiguration.",
    tag: "Software",
  },
  {
    icon: "CpuChipIcon",
    title: "BIOS-Update",
    desc: "Aktuelles BIOS flashen, XMP/EXPO für RAM aktivieren, optimale Grundeinstellungen setzen.",
    tag: "Tuning",
  },
  {
    icon: "ArrowsPointingInIcon",
    title: "Kabelmanagement",
    desc: "Sauber verlegte Kabel, Klettverschlüsse, optimale Luftzirkulation und ein makelloses Erscheinungsbild.",
    tag: "Ästhetik",
  },
  {
    icon: "SparklesIcon",
    title: "RGB-Einrichtung",
    desc: "Lüfter, Streifen und Komponenten synchronisieren – per Software oder ARGB-Controller.",
    tag: "Ästhetik",
  },
  {
    icon: "BoltIcon",
    title: "Gaming-Optimierung",
    desc: "Windows-Optimierungen, unnötige Dienste deaktivieren, FPS-Boost durch gezielte Tweaks.",
    tag: "Performance",
  },
  {
    icon: "ArrowUpCircleIcon",
    title: "Reinigung & Upgrade",
    desc: "Bestehenden PC reinigen, Wärmeleitpaste wechseln, RAM/SSD/GPU upgraden und neu konfigurieren.",
    tag: "Service",
  },
];

export default function LeistungenSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );
    sectionRef.current?.querySelectorAll(".reveal-hidden").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="leistungen" ref={sectionRef} className="relative py-28 lg:py-36 px-6 lg:px-12 bg-[#0A0A14] overflow-hidden">
      <div className="orb w-[400px] h-[400px] bg-blue-500/6 bottom-0 left-0" />

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-6 reveal-hidden">
          <div>
            <div className="section-label mb-4">Leistungen</div>
            <h2 className="font-display font-extrabold text-[clamp(2rem,4vw,3.2rem)] text-white leading-tight">
              Was ich für dich{" "}
              <span className="gradient-text-blue">erledige</span>
            </h2>
          </div>
          <p className="text-white/45 max-w-md text-sm leading-relaxed">
            Jede Leistung wird sauber und professionell durchgeführt. Kein Halbwissen, kein Zeitdruck.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Large featured card */}
          <div
            className="service-card reveal-hidden lg:col-span-2 lg:row-span-2 flex flex-col justify-between min-h-[260px]"
            style={{ transitionDelay: "0ms" }}
          >
            <div>
              <div className="inline-flex w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500/25 to-blue-500/15 border border-violet-500/25 items-center justify-center mb-6">
                <Icon name="WrenchScrewdriverIcon" size={26} className="text-violet-400" />
              </div>
              <h3 className="font-display font-bold text-xl text-white mb-3">PC-Zusammenbau</h3>
              <p className="text-white/45 text-sm leading-relaxed">
                Vollständiger Zusammenbau deines Systems – von CPU-Montage über Kühler bis hin zu Grafikkarte und Speicher. Ich achte auf jeden Detail und jede Schraube.
              </p>
            </div>
            <div className="mt-6 pt-5 border-t border-white/[0.06] flex items-center justify-between">
              <span className="font-mono text-[10px] text-violet-400 uppercase tracking-widest">Kernleistung</span>
              <span className="font-mono text-[10px] text-white/25 uppercase tracking-widest">ab 120 CHF</span>
            </div>
          </div>

          {/* Budget PC Build featured card */}
          <div
            className="service-card reveal-hidden lg:col-span-2 flex flex-col justify-between min-h-[200px] border-green-500/20 bg-gradient-to-br from-green-500/8 to-blue-500/5"
            style={{ transitionDelay: "70ms" }}
          >
            <div>
              <div className="flex items-start justify-between mb-4">
                <div className="inline-flex w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/25 to-blue-500/15 border border-green-500/25 items-center justify-center">
                  <Icon name="CurrencyEuroIcon" size={22} className="text-green-400" />
                </div>
                <span className="font-mono text-[9px] text-green-400 uppercase tracking-widest bg-green-500/10 border border-green-500/20 px-2 py-1 rounded-full">Neu</span>
              </div>
              <h3 className="font-display font-bold text-lg text-white mb-2">Budget PC Build</h3>
              <p className="text-white/45 text-xs leading-relaxed">
                Du gibst mir dein Budget, ich suche die passenden Komponenten aus und baue den PC für dich zusammen. Alles aus einer Hand.
              </p>
            </div>
            <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-center justify-between">
              <span className="font-mono text-[10px] text-green-400 uppercase tracking-widest">Komplett-Service</span>
              <span className="font-mono text-[10px] text-white/25 uppercase tracking-widest">100 CHF + Build-Paket</span>
            </div>
          </div>

          {/* Remaining cards */}
          {leistungen.slice(1).map((l, i) => (
            <div
              key={l.title}
              className="service-card reveal-hidden"
              style={{ transitionDelay: `${(i + 1) * 70}ms` }}
            >
              <div className="inline-flex w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500/15 to-blue-500/10 border border-violet-500/15 items-center justify-center mb-4">
                <Icon name={l.icon as any} size={18} className="text-violet-400" />
              </div>
              <h3 className="font-display font-semibold text-base text-white mb-2">{l.title}</h3>
              <p className="text-white/40 text-xs leading-relaxed">{l.desc}</p>
              <div className="mt-4 pt-3 border-t border-white/[0.04]">
                <span className="font-mono text-[9px] text-blue-400/70 uppercase tracking-widest">{l.tag}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}